import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  mergeClasses,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  WORK_AVAILABILITY_PRIORITIES,
  formatDate,
  formatWorkAvailabilityDuration,
  isValidDate,
  isWorkAvailabilityPriority,
  workAvailabilityDurations,
} from '@moko/utils';
import { TimezoneSelect, isValidTimeZone } from '../../TimezoneSelect';
import {
  TimeSelect,
  isValidTimeOfDay,
  timeOfDayToSeconds,
} from '../../TimeSelect';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFormLayoutStyles } from '../../hooks/useFormLayoutStyles';
import {
  startOfDay,
  type WorkAvailabilityFormValues,
} from './workAvailabilityFormValues';

export interface WorkAvailabilityFormProps {
  disabled: boolean;
}

export const WorkAvailabilityForm = ({
  disabled,
}: WorkAvailabilityFormProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const styles = useFormLayoutStyles();
  const { control, getValues, trigger, watch, formState } =
    useFormContext<WorkAvailabilityFormValues>();
  const { errors, isSubmitted } = formState;
  const isAllDay = watch('isAllDay');
  const priority = watch('priority');
  const priorityOption = WORK_AVAILABILITY_PRIORITIES.find(
    (option) => option.value === priority
  );

  return (
    <>
      <Controller
        control={control}
        name="date"
        rules={{
          required: t('dateRequired', 'Date is required.'),
          validate: (value) =>
            isValidDate(value) || t('dateRequired', 'Date is required.'),
        }}
        render={({ field }) => (
          <Field
            label={t('date', 'Date')}
            required
            validationState={errors.date ? 'error' : 'none'}
            validationMessage={errors.date?.message}
          >
            <DatePicker
              style={{ width: '100%' }}
              value={isValidDate(field.value) ? field.value : undefined}
              formatDate={(date) => (date ? formatDate(date) : '')}
              disabled={disabled}
              onSelectDate={(selected) => {
                field.onChange(selected ? startOfDay(selected) : null);
              }}
            />
          </Field>
        )}
      />

      {!isAllDay && (
        <div
          className={mergeClasses(
            styles.twoCol,
            isMobile && styles.twoColStacked
          )}
        >
          <div className={styles.col}>
            <Controller
              control={control}
              name="startTime"
              rules={{
                required: t('startTimeRequired', 'Start time is required.'),
                validate: (value) =>
                  isValidTimeOfDay(value) ||
                  t('startTimeInvalid', 'Start time is invalid.'),
              }}
              render={({ field }) => (
                <Field
                  label={t('startTime', 'Start time')}
                  required
                  validationState={errors.startTime ? 'error' : 'none'}
                  validationMessage={errors.startTime?.message}
                >
                  <TimeSelect
                    value={field.value}
                    disabled={disabled}
                    onChange={(next) => {
                      field.onChange(next);
                      if (isSubmitted) void trigger('endTime');
                    }}
                  />
                </Field>
              )}
            />
          </div>
          <div className={styles.col}>
            <Controller
              control={control}
              name="endTime"
              rules={{
                required: t('endTimeRequired', 'End time is required.'),
                validate: (value) => {
                  if (!isValidTimeOfDay(value)) {
                    return t('endTimeInvalid', 'End time is invalid.');
                  }
                  const start = timeOfDayToSeconds(getValues('startTime'));
                  const end = timeOfDayToSeconds(value);
                  if (start != null && end != null && end <= start) {
                    return t(
                      'startMustBeBeforeEnd',
                      'Start must be earlier than End.'
                    );
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Field
                  label={t('endTime', 'End time')}
                  required
                  validationState={errors.endTime ? 'error' : 'none'}
                  validationMessage={errors.endTime?.message}
                >
                  <TimeSelect
                    value={field.value}
                    disabled={disabled}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          </div>
        </div>
      )}

      <Controller
        control={control}
        name="isAllDay"
        render={({ field }) => (
          <Field
            label={t('allDay', 'All day')}
            orientation="horizontal"
            className={styles.toggleField}
          >
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={disabled}
            />
          </Field>
        )}
      />

      <Controller
        control={control}
        name="duration"
        render={({ field }) => (
          <Field label={t('duration', 'Duration')}>
            <Dropdown
              style={{ minWidth: 0, width: '100%' }}
              placeholder={t('selectDuration', 'Select duration')}
              value={
                field.value == null
                  ? ''
                  : formatWorkAvailabilityDuration(field.value)
              }
              selectedOptions={field.value == null ? [] : [String(field.value)]}
              onOptionSelect={(_, data) => {
                if (!data.optionValue) {
                  field.onChange(null);
                  return;
                }
                field.onChange(Number(data.optionValue));
              }}
              disabled={disabled}
            >
              <Option value="">{t('none', 'None')}</Option>
              {workAvailabilityDurations.map((minutes) => (
                <Option key={minutes} value={String(minutes)}>
                  {formatWorkAvailabilityDuration(minutes)}
                </Option>
              ))}
            </Dropdown>
          </Field>
        )}
      />

      <div
        className={mergeClasses(
          styles.twoCol,
          isMobile && styles.twoColStacked
        )}
      >
        <div className={styles.col}>
          <Controller
            control={control}
            name="timezoneId"
            rules={{
              required: t(
                'timezoneInvalid',
                'Timezone must be a valid timezone ID.'
              ),
              validate: (value) =>
                isValidTimeZone(value) ||
                t('timezoneInvalid', 'Timezone must be a valid timezone ID.'),
            }}
            render={({ field }) => (
              <Field
                label={t('timezone', 'Timezone')}
                required
                validationState={errors.timezoneId ? 'error' : 'none'}
                validationMessage={errors.timezoneId?.message}
              >
                <TimezoneSelect
                  value={field.value}
                  placeholder={t('selectTimezone', 'Select timezone')}
                  disabled={disabled}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />
        </div>
        <div className={styles.col}>
          <Controller
            control={control}
            name="priority"
            rules={{
              validate: (value) =>
                isWorkAvailabilityPriority(value) ||
                t('priorityInvalid', 'Priority is invalid.'),
            }}
            render={({ field }) => (
              <Field
                label={t('priority', 'Priority')}
                required
                validationState={errors.priority ? 'error' : 'none'}
                validationMessage={errors.priority?.message}
              >
                <Dropdown
                  style={{ minWidth: 0, width: '100%' }}
                  value={
                    priorityOption
                      ? t(priorityOption.labelKey, priorityOption.labelFallback)
                      : String(priority)
                  }
                  selectedOptions={[String(field.value)]}
                  onOptionSelect={(_, data) => {
                    if (isWorkAvailabilityPriority(data.optionValue)) {
                      field.onChange(data.optionValue);
                    }
                  }}
                  disabled={disabled}
                >
                  {WORK_AVAILABILITY_PRIORITIES.map((option) => (
                    <Option key={option.value} value={String(option.value)}>
                      {t(option.labelKey, option.labelFallback)}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
          />
        </div>
      </div>

      <Controller
        control={control}
        name="sourceControlled"
        render={({ field }) => (
          <Field label={t('source', 'Source')}>
            <Input
              value={field.value ?? ''}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
              disabled={disabled}
            />
          </Field>
        )}
      />

      <Controller
        control={control}
        name="status"
        render={({ field }) => (
          <Field
            label={t('status', 'Status')}
            orientation="horizontal"
            className={styles.toggleField}
          >
            <Switch
              checked={field.value}
              label={
                field.value ? t('active', 'Active') : t('inactive', 'Inactive')
              }
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={disabled}
            />
          </Field>
        )}
      />
    </>
  );
};
